import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Package, Clock, CheckCircle, DollarSign, TrendingDown, MapPin, Phone, Pencil, Check, X, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RiderDashboard = () => {
  const { riderId } = useParams();
  const navigate = useNavigate();
  const [rider, setRider] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCharge, setEditingCharge] = useState(null); // order id being edited
  const [editChargeValue, setEditChargeValue] = useState("");

  useEffect(() => {
    fetchRiderData();
  }, [riderId]);

  const fetchRiderData = async () => {
    try {
      const [ridersRes, dashboardRes, ordersRes] = await Promise.all([
        axios.get(`${API}/riders`),
        axios.get(`${API}/riders/${riderId}/dashboard`),
        axios.get(`${API}/orders?rider_id=${riderId}`),
      ]);

      const riderData = ridersRes.data.find((r) => r.id === riderId);
      if (!riderData) {
        toast.error("Rider not found");
        navigate("/riders");
        return;
      }

      setRider(riderData);
      setDashboard(dashboardRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error("Error fetching rider data:", error);
      toast.error("Failed to fetch rider data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCharge = async (orderId) => {
    const val = parseFloat(editChargeValue);
    if (isNaN(val) || val < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      await axios.put(`${API}/orders/${orderId}`, { delivery_charge: val });
      toast.success("Delivery charge updated");
      setEditingCharge(null);
      fetchRiderData();
    } catch (error) {
      toast.error("Failed to update charge");
    }
  };

  const handleToggleChargeStatus = async (orderId, currentStatus) => {
    const newStatus = currentStatus === "collected" ? "pending" : "collected";
    try {
      await axios.put(`${API}/orders/${orderId}`, { delivery_charge_status: newStatus });
      toast.success(`Charge marked as ${newStatus}`);
      fetchRiderData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-md"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!rider || !dashboard) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Rider not found</p>
      </div>
    );
  }

  const metrics = [
    { label: "Total Orders", value: dashboard.total_orders, icon: Package, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Pending Orders", value: dashboard.pending_orders, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Completed Orders", value: dashboard.completed_orders, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Charges Pending", value: `₹${dashboard.delivery_charges_pending.toFixed(2)}`, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Charges Collected", value: `₹${dashboard.delivery_charges_collected.toFixed(2)}`, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Cash Collected", value: `₹${dashboard.cash_collected.toFixed(2)}`, icon: DollarSign, color: "text-green-600", bg: "bg-green-50" },
    { label: "Cash Spent", value: `₹${dashboard.cash_spent.toFixed(2)}`, icon: TrendingDown, color: "text-red-600", bg: "bg-red-50" },
  ];

  const statusColors = {
    pending: "bg-slate-100 text-slate-700",
    assigned: "bg-blue-100 text-blue-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
  };

  const chargeColors = {
    pending: "bg-amber-100 text-amber-700",
    collected: "bg-emerald-100 text-emerald-700",
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="rider-dashboard-page">
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate("/riders")}
          className="mb-4 hover:bg-slate-100"
          data-testid="back-to-riders-btn"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Riders
        </Button>

        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
            {rider.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="rider-name">
              {rider.name}
            </h1>
            <p className="text-slate-500 mt-1">
              {rider.phone} | Vehicle: {rider.vehicle_number}
              {rider.no_service_day && <span className="ml-2 text-red-500">| Off: {rider.no_service_day}</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="rider-metrics">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              className="bg-white border border-slate-200 shadow-sm rounded-md p-4 hover:border-slate-300 transition-all"
              data-testid={`rider-metric-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{metric.label}</p>
                  <p className="text-2xl font-extrabold tracking-tighter text-slate-900 mt-1">{metric.value}</p>
                </div>
                <div className={`${metric.bg} ${metric.color} p-2 rounded-md`}>
                  <Icon size={18} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Rider Cash Summary */}
      <div className="bg-slate-900 border border-slate-700 rounded-md p-6" data-testid="rider-cash-summary">
        <h2 className="text-lg font-semibold text-slate-300 mb-4 flex items-center gap-2">
          <Wallet size={20} />
          Cash Summary — {rider.name}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold">Delivery Charges Collected</p>
            <p className="text-2xl font-extrabold text-emerald-400 mt-1">₹{dashboard.delivery_charges_collected.toFixed(2)}</p>
            <p className="text-xs text-slate-600 mt-0.5">Rider's share (service fees)</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold">Cash Collected</p>
            <p className="text-2xl font-extrabold text-orange-400 mt-1">₹{dashboard.cash_collected.toFixed(2)}</p>
            <p className="text-xs text-slate-600 mt-0.5">From customers (COD, products)</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold">Expenses</p>
            <p className="text-2xl font-extrabold text-red-400 mt-1">₹{dashboard.cash_spent.toFixed(2)}</p>
            <p className="text-xs text-slate-600 mt-0.5">Gas, food, etc.</p>
          </div>
          <div className="border-l border-slate-700 pl-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Amount to Submit</p>
            <p className={`text-3xl font-extrabold mt-1 ${(dashboard.total_in_hand || 0) >= 0 ? "text-white" : "text-red-400"}`}>
              ₹{(dashboard.total_in_hand || 0).toFixed(2)}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">Charges + Cash − Expenses</p>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-md" data-testid="rider-orders-section">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">
            Orders ({orders.length})
          </h2>
        </div>
        {orders.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No orders assigned yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rider-orders-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left p-3 font-semibold text-slate-600">Customer</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Order Details</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Charge</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Status</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Charge Status</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Cash</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Expense</th>
                  <th className="text-left p-3 font-semibold text-slate-600">COD</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`order-row-${order.id}`}>
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{order.customer_name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                        <Phone size={10} />
                        {order.customer_phone}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-start gap-1 text-slate-600">
                        <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
                        <span className="line-clamp-2">{order.order_details || order.delivery_address || "-"}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      {editingCharge === order.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500 font-semibold">₹</span>
                          <Input
                            type="number"
                            value={editChargeValue}
                            onChange={(e) => setEditChargeValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleUpdateCharge(order.id);
                              if (e.key === "Escape") setEditingCharge(null);
                            }}
                            className="w-20 h-7 text-sm px-1"
                            autoFocus
                            data-testid={`edit-charge-input-${order.id}`}
                          />
                          <button
                            onClick={() => handleUpdateCharge(order.id)}
                            className="p-0.5 rounded hover:bg-emerald-100 text-emerald-600"
                            data-testid={`save-charge-btn-${order.id}`}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => setEditingCharge(null)}
                            className="p-0.5 rounded hover:bg-red-100 text-red-500"
                            data-testid={`cancel-charge-btn-${order.id}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingCharge(order.id);
                            setEditChargeValue(order.delivery_charge.toString());
                          }}
                          className="inline-flex items-center gap-1 font-semibold text-slate-900 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors group"
                          data-testid={`edit-charge-trigger-${order.id}`}
                        >
                          ₹{order.delivery_charge.toFixed(2)}
                          <Pencil size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500" />
                        </button>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[order.status] || "bg-slate-100"}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleToggleChargeStatus(order.id, order.delivery_charge_status)}
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${
                          order.delivery_charge_status === "collected"
                            ? "bg-emerald-100 text-emerald-700 hover:ring-emerald-300"
                            : "bg-amber-100 text-amber-700 hover:ring-amber-300"
                        }`}
                        data-testid={`toggle-charge-status-${order.id}`}
                        title={`Click to mark as ${order.delivery_charge_status === "collected" ? "pending" : "collected"}`}
                      >
                        {order.delivery_charge_status}
                      </button>
                    </td>
                    <td className="p-3 text-slate-700">
                      {order.cash_collected > 0 ? (
                        <div>
                          <span className="font-medium">₹{order.cash_collected.toFixed(2)}</span>
                          {order.cash_collected_reason && (
                            <div className="text-xs text-slate-400 mt-0.5">{order.cash_collected_reason}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-700">
                      {order.cash_spent > 0 ? (
                        <div>
                          <span className="font-medium text-red-600">₹{order.cash_spent.toFixed(2)}</span>
                          {order.cash_spent_reason && (
                            <div className="text-xs text-slate-400 mt-0.5">{order.cash_spent_reason}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      {order.is_cod ? (
                        <span className="font-medium text-orange-600">₹{order.cod_amount.toFixed(2)}</span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiderDashboard;
